/**
 * lib/openingBalances.ts
 * ------------------------------------------------------------------
 * Bringing the school's position across from Pastel, as ONE journal.
 *
 * PURE. Invoices in, journal lines out. No Firestore, no clock — the entry
 * that decides what the ledger says the school is owed on day one is worked
 * out where it can be checked line by line, offline, before anything writes.
 *
 * THE ENTRY:
 *
 *   Dr  Accounts Receivable — School Fees      total owed, GROSS
 *   Cr  Fees Received in Advance               total in credit, GROSS
 *   Cr  Accumulated Fund                       the balancing figure
 *
 * GROSS ON BOTH SIDES, NEVER NETTED. At the 11 Aug rebaseline 276 families
 * owed P6.35m and 178 were in credit by P3.02m. Netting those to a single
 * P3.33m receivable understates BOTH what families owe the school and what
 * the school is holding on their behalf, by three million pula each — and
 * the second of those is a liability, not a smaller asset. Presenting them
 * on opposite sides of the balance sheet is not a preference; it is what the
 * two figures are.
 *
 * THE CUT-OVER DATE is the day the portal becomes the book of record. Every
 * invoice raised on or before it belongs to the opening position and is
 * carried by THIS journal; everything after it posts through its own trigger
 * from its own source document. Getting that boundary wrong in either
 * direction double-counts or loses the lot, which is why the posting
 * triggers refuse an invoice dated on or before the cut-over rather than
 * quietly posting it a second time.
 *
 * IT IS IDEMPOTENT. The journal id is derived from the cut-over date, so
 * re-running against the same date overwrites the same journal instead of
 * adding a second one. Change the date and you get a different journal —
 * which is correct, and is also why the screen makes you confirm the date.
 * ------------------------------------------------------------------ */
import type { Invoice } from '@/lib/financeCore';
import type { JournalLine } from '@/lib/gl';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/** One pupil's position at cut-over. */
export interface OpeningRow {
  studentId: string;
  studentName?: string;
  invoiceNo?: string;
  invoiceId: string;
  /** Positive = owes the school. Negative = the school holds their money. */
  balance: number;
}

export interface OpeningPosition {
  /** Invoices dated on or before the cut-over that carry a balance. */
  debtors: OpeningRow[];
  credits: OpeningRow[];
  /** Gross total owed to the school. */
  totalDebtors: number;
  /** Gross total held on families' behalf, as a POSITIVE figure. */
  totalCredits: number;
  /** debtors − credits. The figure a netted entry would have used. */
  net: number;
  /** Invoices in range that come to nil — carried for the count only. */
  settledCount: number;
  /** Invoices whose stored balance disagrees with total − paid − adjusted. */
  discrepancies: { invoiceId: string; invoiceNo?: string; stored: number; computed: number }[];
}

/** total − paid − adjusted, which is what a balance is. */
export function computedBalance(inv: Invoice): number {
  return round2(
    (Number(inv.total) || 0) - (Number(inv.paid) || 0) - (Number(inv.adjusted) || 0),
  );
}

/**
 * Everything the school is owed, and everything it holds, at the cut-over.
 *
 * VOID INVOICES ARE EXCLUDED — a void invoice is not a debt.
 *
 * THE STORED BALANCE IS BELIEVED, but a disagreement with total − paid −
 * adjusted is REPORTED rather than silently corrected. If the two differ,
 * something upstream is wrong and quietly using the arithmetic would take
 * the school's opening position from a figure nobody checked. The list is
 * short enough to look at, and it should be looked at.
 */
export function buildOpeningPosition(
  invoices: Invoice[],
  cutoverDate: string,
): OpeningPosition {
  const cut = String(cutoverDate).slice(0, 10);
  const debtors: OpeningRow[] = [];
  const credits: OpeningRow[] = [];
  const discrepancies: OpeningPosition['discrepancies'] = [];
  let settledCount = 0;

  for (const inv of invoices) {
    if (inv.status === 'void') continue;
    const issued = String(inv.issuedAt ?? '').slice(0, 10);
    // No date means it cannot be placed either side of the boundary, and
    // guessing would either double-count it or lose it.
    if (!issued || issued > cut) continue;

    const stored = round2(Number(inv.balance) || 0);
    const computed = computedBalance(inv);
    if (Math.abs(stored - computed) > 0.005) {
      discrepancies.push({ invoiceId: inv.id, invoiceNo: inv.invoiceNo, stored, computed });
    }

    const row: OpeningRow = {
      studentId: inv.studentId,
      ...(inv.studentName ? { studentName: inv.studentName } : {}),
      ...(inv.invoiceNo ? { invoiceNo: inv.invoiceNo } : {}),
      invoiceId: inv.id,
      balance: stored,
    };

    if (stored > 0) debtors.push(row);
    else if (stored < 0) credits.push(row);
    else settledCount += 1;
  }

  const totalDebtors = round2(debtors.reduce((a, r) => a + r.balance, 0));
  const totalCredits = round2(Math.abs(credits.reduce((a, r) => a + r.balance, 0)));

  debtors.sort((a, b) => b.balance - a.balance);
  credits.sort((a, b) => a.balance - b.balance);

  return {
    debtors,
    credits,
    totalDebtors,
    totalCredits,
    net: round2(totalDebtors - totalCredits),
    settledCount,
    discrepancies,
  };
}

export interface OpeningAccounts {
  /** AR_FEES */
  receivables: string;
  /** FEES_IN_ADVANCE */
  feesInAdvance: string;
  /** ACCUM_FUND */
  accumulatedFund: string;
}

/**
 * The journal.
 *
 * The accumulated fund takes the BALANCING FIGURE, whichever way round it
 * falls: a school whose families are in credit overall opens with a debit to
 * the fund rather than a credit. Deriving it rather than assuming its side
 * is what makes this work for a school that is not LKC.
 *
 * A side that comes to nothing produces no line. A zero-value line on an
 * opening journal is noise on the one entry every auditor will read first.
 */
export function openingJournalLines(
  position: OpeningPosition,
  accounts: OpeningAccounts,
): JournalLine[] {
  const lines: JournalLine[] = [];

  if (position.totalDebtors > 0) {
    lines.push({
      accountCode: accounts.receivables,
      dr: position.totalDebtors,
      cr: 0,
      narration: `Fees owed at cut-over — ${position.debtors.length} account${position.debtors.length === 1 ? '' : 's'}`,
    });
  }

  if (position.totalCredits > 0) {
    lines.push({
      accountCode: accounts.feesInAdvance,
      dr: 0,
      cr: position.totalCredits,
      narration: `Fees held in advance at cut-over — ${position.credits.length} account${position.credits.length === 1 ? '' : 's'}`,
    });
  }

  const balancing = round2(position.totalDebtors - position.totalCredits);
  if (balancing !== 0) {
    lines.push({
      accountCode: accounts.accumulatedFund,
      dr: balancing < 0 ? round2(-balancing) : 0,
      cr: balancing > 0 ? balancing : 0,
      narration: 'Opening position brought forward',
    });
  }

  return lines;
}

/**
 * The source id for the opening journal.
 *
 * Derived from the cut-over date so re-running against the same date
 * overwrites the same journal rather than posting a second one. A different
 * date is a different journal, deliberately: it is a different statement
 * about when the books changed hands.
 */
export function openingSourceId(cutoverDate: string): string {
  return `opening-${String(cutoverDate).slice(0, 10)}`;
}

/**
 * Why this opening position cannot be posted, or null if it can.
 *
 * The discrepancy check is a REFUSAL, not a warning. This is the entry every
 * later figure is measured from; posting it while the sub-ledger disagrees
 * with itself puts a number nobody has verified at the base of the accounts,
 * and every report after it inherits the doubt.
 */
export function openingProblem(position: OpeningPosition): string | null {
  if (position.debtors.length === 0 && position.credits.length === 0) {
    return 'No invoice on or before that date carries a balance. Check the cut-over date.';
  }
  if (position.discrepancies.length > 0) {
    const n = position.discrepancies.length;
    return `${n} invoice${n === 1 ? '' : 's'} ${n === 1 ? 'has a' : 'have'} stored balance that disagrees with total less payments and credits. `
      + 'Fix those before taking on the opening position — every later figure is measured from this entry.';
  }
  return null;
}
